import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { FormInput, FormSelect } from '../forms';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from "../../hooks/useAuth";

const TIME_SLOTS = [
  '09:00', '10:00', '11:00', '12:00',
  '14:00', '15:00', '16:00'
];

const EVENT_TYPES = [
  { value: 'holiday', label: 'Holiday' },
  { value: 'event', label: 'Event' }
];

export const CalendarEventForm = ({ inModal = false, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { post } = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    startTime: '',
    endTime:'',
    type: 'holiday',
  });
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      if (formData.startTime >= formData.endTime) {
        setError("End time must be after the start time.");
        return;
      }

      const response = await post('/calendar-events', formData);

      if (response.data?.status === 'success') {
        setSuccess(true);
        if (onSuccess) onSuccess(response.data);
        if (onClose) onClose();
        navigate(currentUser.role === "admin" ? "/admin/calendar-events" : "/receptionist/calendar-events");
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create calendar event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white dark:bg-slate-800 rounded-lg shadow transition-colors">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Create Calendar Event</h2>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
          >
            ×
          </button>
        )}
      </div>

      {error && (
        <div className="px-6 pt-4">
          <div className="bg-red-50 dark:bg-red-900/10 border-l-4 border-red-400 p-4 rounded-md flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
            <p className="ml-3 text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="px-6 pt-4">
          <div className="bg-green-50 dark:bg-green-900/10 border-l-4 border-green-400 p-4 rounded-md flex items-center">
            <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
            <p className="ml-3 text-sm text-green-700 dark:text-green-300">Event created successfully!</p>
          </div>
        </div>
      )}

      <div className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <FormInput
            type="text"
            label="Event Name"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            required
          />

          <FormInput
            type="date"
            label="Event Date"
            name="date"
            value={formData.date}
            onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
            min={new Date().toISOString().split('T')[0]}
            required
          />

          <FormInput
            type="time"
            label="Start Time"
            name="startTime"
            value={formData.startTime}
            onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
          />

          <FormInput
            type="time"
            label="End Time"
            name="endTime"
            value={formData.endTime}
            onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
          />

          <FormSelect
            label="Event Type"
            name="type"
            value={formData.type}
            onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
            options={EVENT_TYPES}
            required
          />

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.name || !formData.date || !formData.startTime || !formData.endTime}
              className="px-6 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700 dark:hover:bg-blue-500 disabled:bg-blue-300 dark:disabled:bg-blue-800 transition-colors"
            >
              {loading ? 'Processing...' : 'Create Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CalendarEventForm;